import React, { useEffect } from 'react';
import { FacebookLoading } from '@givelify/givelify-ui';
import { styled, TablePagination } from '@mui/material';
import { IntegrationTypes } from 'pages/integrations/types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AppState, useAppDispatch } from 'store';
import { GetReportsAsync } from 'store/integrations/thunks';
import IntegrationReportsTable from './IntegrationReportsTable';
import NoIntegrationReport from './NoIntegrationReport';

const StyledTablePagination = styled(TablePagination)(() => ({
    '& .MuiIconButton-root': {
        padding: 12,
    },
})) as typeof TablePagination;

const TopMarginDiv = styled('div')(({ theme }) => ({
    marginTop: theme.spacing(3),
}));

const BorderedDiv = styled('div')(() => ({
    borderBottom: '1px solid #e0e0e0',
}));

interface IntegrationReportContentProps {
    integration: IntegrationTypes;
    isReadOnly: boolean;
}

const Loading = (
    <TopMarginDiv>
        <FacebookLoading borderRadius={0} height={69} width="100%" />
        <BorderedDiv />
        <FacebookLoading borderRadius={0} height={69} width="100%" />
        <BorderedDiv />
        <FacebookLoading borderRadius={0} height={69} width="100%" />
        <BorderedDiv />
    </TopMarginDiv>
);

const pageSize = 8;

const IntegrationReportContent: React.FCC<IntegrationReportContentProps> = ({
    integration,
    isReadOnly,
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const { reports, reportsLoading, pagination, page } = useSelector(
        (state: AppState) => ({
            reports: state.Integrations.reports,
            reportsLoading: state.Integrations.reportsLoading,
            pagination: state.Integrations.reportsPagination,
            page: state.Integrations.page,
        }),
    );

    const onChangePage = (e: unknown, pageNumber: number) => {
        dispatch(GetReportsAsync(pageNumber + 1, pageSize));
    };

    useEffect(() => {
        dispatch(GetReportsAsync(1, pageSize));
    }, [dispatch]);

    if (reportsLoading) return Loading;
    if (reports.length === 0) return <NoIntegrationReport />;

    const count = pagination.total;

    return (
        <div>
            <IntegrationReportsTable
                integration={integration}
                isReadOnly={isReadOnly}
                pageNumber={page}
                rows={reports}
            />
            <StyledTablePagination
                component="div"
                count={count}
                onPageChange={onChangePage}
                page={page - 1}
                rowsPerPage={pageSize}
                rowsPerPageOptions={[count]}
                slotProps={{
                    actions: {
                        previousButton: {
                            'aria-label': t('labels.previous'),
                        },
                        nextButton: {
                            'aria-label': t('labels.next'),
                        },
                    },
                }}
            />
        </div>
    );
};

export default IntegrationReportContent;
